// store/financialTypes.ts
// Persisted financial-profile shapes. Every type the reducers, selectors, and
// UI speak lives here so the slice, migrations, and thunks share one source.

// Legacy three-value frequency. Still a named export because old call sites
// (toAnnual, charts, old persisted payloads) speak it. The wider expense/income
// sets below overlap on weekly/monthly/yearly, so legacy data stays valid.
export type Frequency = 'weekly' | 'monthly' | 'yearly';

// Income adds a one-time entry (bonus/windfall), spread over spreadMonths into a
// monthly-equivalent. Recurring values are a subset of Frequency.
export type IncomeFrequency = 'weekly' | 'monthly' | 'yearly' | 'one_time';

// Income category is a LABEL carrying a suggested tax default (see
// INCOME_CATEGORY_TAX_DEFAULT in meta.ts). Three values: job, self-employment
// (folds side/freelance/business), other. Legacy rows migrate in migrateIncomeEntry.
export type IncomeCategory = 'job_income' | 'self_employment' | 'other';

// Richer expense frequency set. one_time is spread over spreadMonths into a
// monthly-equivalent (see toAnnual).
export type ExpenseFrequency =
	| 'weekly'
	| 'biweekly'
	| 'monthly'
	| 'quarterly'
	| 'yearly'
	| 'one_time';

// Behavior/flexibility grouping. 'other' is the destination for migrated legacy
// expenses. Future Self is deliberately NOT here: investing/savings/payoff are
// surplus ALLOCATION, not expenses, so they never reduce freedom fuel.
export type ExpenseGroup =
	| 'fixed_essentials'
	| 'flexible_lifestyle'
	| 'debt_minimums'
	| 'irregular_costs'
	| 'other';

// Optional self-assessed flexibility. Group says WHAT the spending is,
// flexibility says how movable it is. Undefined on migrated rows = "needs review".
export type ExpenseFlexibility = 'low' | 'medium' | 'high';

export interface IncomeEntry {
	id: string;
	name: string;
	amount: string;
	frequency: IncomeFrequency;
	// Only meaningful when frequency === 'one_time'. Spreads the amount over this
	// many months to a monthly-equivalent (default 12).
	spreadMonths?: number;
	// Label-only. Undefined reads as "Other income" and changes no math.
	category?: IncomeCategory;
	// Per-source tax treatment. Undefined inherits assumptions.incomeMode.
	incomeMode?: IncomeMode;
	// Per-source estimated tax rate (pct string), only meaningful when this row's
	// incomeMode === 'preTax'. Undefined falls back to the profile default.
	taxRate?: string;
	notes?: string;
}

export interface ExpenseEntry {
	id: string;
	name: string;
	amount: string;
	frequency: ExpenseFrequency;
	// group defaults to 'other' on migration; flexibility is undefined until
	// reviewed; spreadMonths is only meaningful when frequency === 'one_time'.
	group: ExpenseGroup;
	flexibility?: ExpenseFlexibility;
	spreadMonths?: number;
	// Whether this row counts toward the monthly spending total (and the surplus /
	// FI math). Defaults true. Lets a user park a row without moving their number.
	includeInMonthlySpending: boolean;
	// A payment that ENDS (mortgage, car note, lease, student loan). Stored as a
	// CALENDAR year string ("2034"), never a years-from-now offset, so persisted
	// facts don't drift as real time passes. Only meaningful on recurring
	// frequencies; undefined = ongoing.
	endYear?: string;
	// When this payment services a loan, the row IS the loan record: remaining
	// principal + APR live here. loanBalance also nets against net worth (see
	// linkedAssetId); a balance with no linked asset is unsecured debt.
	loanBalance?: string;
	loanRatePercent?: string;
	// The dollars of this payment actually applied to the loan each month
	// (principal + interest only). Honesty checks that amortize the balance use
	// this when present and treat the row amount as an upper bound otherwise.
	loanPaymentMonthly?: string;
	// The LifestyleAsset this loan is secured against (expense → asset, many loans
	// per asset). A dangling id reads as unsecured. Undefined = not tied to an asset.
	linkedAssetId?: string;
	notes?: string;
}

// Surplus allocation. Income − spending = surplus; surplus is allocated here.
// These entries describe where surplus goes; they never reduce surplus itself.
export type SurplusAllocationGroup =
	| 'investing'
	| 'cash_savings'
	| 'emergency_fund'
	| 'extra_debt_payoff'
	| 'unassigned';

export interface SurplusAllocationEntry {
	id: string;
	name: string;
	amount: string;
	frequency: Frequency; // recurring; no one-time spread needed
	group: SurplusAllocationGroup;
	notes?: string;
}

// USER-TAGGED, not inferred from the ticker. Defaults to 'single-stock' on add.
export type AssetClass =
	| 'broad-market'
	| 'single-stock'
	| 'bond'
	| 'cash'
	| 'real-estate'
	| 'speculative'
	| 'other';

// A single retained monthly close, kept so the reality-check layer can judge
// whether a 5-year CAGR was measured from a cherry-picked low. t is epoch ms;
// price is the adjusted close.
export interface PriceHistoryPoint {
	t: number;
	price: number;
}

// Provenance of avgPrice. 'user' is a confirmed cost basis and supports
// gain display. 'seeded' means a pre-fix refresh copied the market quote
// into average price; 'unknown' is any other pre-fix value. Neither
// non-user provenance supports gain display until the user confirms what
// they actually paid (any user edit of avgPrice stamps 'user').
// Mirrors AvgPriceSource in @nisk/core/investing (same AssetClass pattern:
// the store stays import-free; core duplicates structurally).
export type AvgPriceSource = 'user' | 'seeded' | 'unknown';

export interface StockHolding {
	id: string;
	symbol: string;
	companyName: string;
	shares: string;
	avgPrice: string;
	monthlyContribution: string;
	// pct string e.g. "14.2" — custom return in Detailed mode. Empty = inherit.
	growthRate: string;
	growthRateSource: 'api' | 'manual' | 'none';
	currentPrice: string; // latest market price
	priceUpdatedAt: number | null; // epoch ms of last price fetch
	assetClass: AssetClass; // drives allocation & drift, not returns
	// Monthly-close window backing an API growth rate. Empty for manual/un-fetched.
	priceHistory: PriceHistoryPoint[];
	// V13 valuation fix. The user's explicit CURRENT value for a quote-less
	// holding (tier 2 of the four-tier policy). Never inferred from avgPrice
	// or cost basis; never migrated from either; always shown with its date.
	manualCurrentValue?: string;
	// Epoch ms of the last deliberate manualCurrentValue edit. null = cleared.
	manualValueUpdatedAt?: number | null;
	// Provenance of avgPrice (see AvgPriceSource above). Undefined only on
	// payloads that predate migration; the valuation helper treats undefined
	// conservatively (no gain display).
	avgPriceSource?: AvgPriceSource;
	// Optional analytics fields; undefined by default. Existing holdings migrate
	// untouched and simply don't surface the insight until the field is filled.
	targetAllocationPct?: string; // drift = current − target
	annualYield?: string; // pct string e.g. "2.5"; drives forecastIncome
	reinvestDividends?: boolean; // DRIP on? affects spendable vs reinvested income
	isEmployer?: boolean; // folds into employer-concentration risk
	// On the synthetic "Future investing plan" holding: the plan is LINKED to the
	// tracked holding with this id (plan owns the contribution, linked holding
	// supplies the growth). Undefined = "Simple investing estimate". Only the plan
	// reads it.
	linkedHoldingId?: string;
	// A holding the user started adding but hasn't chosen a ticker for. Drafts show
	// an inline picker, are EXCLUDED from value/projection, and become 'active'
	// only once a ticker/name is saved. Undefined = active.
	status?: 'draft' | 'active';
	// True when an active holding has a ticker but is waiting for shares + average
	// price (set by changeHoldingTicker/activateHolding, auto-cleared once both are
	// filled). Undefined reads as false.
	awaitingPosition?: boolean;
	// Fingerprints of accepted review warnings; the insight layer re-surfaces a
	// warning once the underlying assumption meaningfully changes. Undefined =
	// nothing reviewed yet.
	reviewedAssumptions?: ReviewedAssumptions;
}

// Per-reason fingerprints of the moment a review warning was accepted. Only the
// three REVIEW-severity kinds can be dismissed; each stores the minimal signal
// needed to detect a meaningful later change. Absence of a key = not reviewed
// (warning active). See holdingReviewSuppressed() in investmentInsights.
export interface ReviewedAssumptions {
	// Reviewed the "manual growth rate" warning at this rate. A materially
	// different rate re-surfaces it.
	manualRate?: { rate: string };
	// Reviewed the "rate may be running hot" window warning against this rate +
	// source. A refetch that changes the rate re-surfaces it.
	flaggedWindow?: { rate: string; source: StockHolding['growthRateSource'] };
	// Reviewed the "stale price" warning at this timestamp. A later refresh that
	// itself goes stale re-surfaces it.
	stalePrice?: { priceUpdatedAt: number | null };
}

// A holding is a real, counted position unless it's an explicit draft. Central
// so value/projection/insight code can't drift on the definition.
export function isDraftHolding(h: Pick<StockHolding, 'status'>): boolean {
	return h.status === 'draft';
}

// ─── Equity grants (RSUs / options / private shares) ───────────────────────────
// Stored alongside holdings, JSON-serializable. Conservative by design:
// vested/liquid value counts toward FI, everything else is opt-in.

export type EquityType = 'rsu' | 'stock-options' | 'private-shares' | 'other';
export type LiquidityStatus =
	| 'liquid'
	| 'public-company'
	| 'private-illiquid'
	| 'unknown';

export interface EquityGrant {
	id: string;
	label: string;
	equityType: EquityType;
	currentEstimatedValue: string; // total paper value
	vestedValue: string;
	unvestedValue: string;
	monthlyVestingValue: string; // value vesting per month going forward
	expectedAnnualGrowthRate: string; // pct string; empty = inherit
	liquidityStatus: LiquidityStatus;
	includeInFIProjection: boolean; // master include flag
	includeOnlyVestedValue: boolean; // when true, only vested counts
}

// Portfolio modeling mode. 'simple' grows the whole investable pile at one
// blended return; 'detailed' models holdings, contributions, and vested equity
// separately, with year-by-year overrides as the market layer.
export type PortfolioMode = 'simple' | 'detailed';

// Year-by-year return overrides. yearOffset 0 = current year, capped 0..5. After
// the last override, projection reverts to the default expected return. pct
// strings so blank fields round-trip cleanly.
export interface ReturnOverride {
	yearOffset: number; // 0..5
	expectedReturnPercent: string;
	note?: string;
}

// Income tax treatment. Flat and lightweight, not a tax engine. 'preTax'
// subtracts a single estimated rate to approximate take-home before surplus /
// savings-rate / FI math. 'afterTax' treats entered income as already banked.
export type IncomeMode = 'afterTax' | 'preTax';

export interface Assumptions {
	returnRate: string;
	withdrawalRate: string;
	incomeGrowthRate: string;
	inflationRate: string; // erosion assumption; defaults '3', blank = off
	projectUntilAge: string; // end age for the projection
	portfolioMode: PortfolioMode;
	returnOverrides: ReturnOverride[]; // 0..6 entries
	incomeMode: IncomeMode; // how to interpret entered income
	estimatedTaxRate: string; // pct string; only applied when incomeMode='preTax'
	// Declared goal age for Simulate's goal-first mode. '' = no target declared.
	targetFreedomAge: string;
}

// Lifestyle assets (home, vehicle, other). Part of net worth but NOT part of the
// invested portfolio: they never grow and never feed the freedom math.
export type LifestyleAssetKind = 'home' | 'vehicle' | 'other';

export interface LifestyleAsset {
	id: string;
	label: string;
	kind: LifestyleAssetKind;
	value: string;
}

export interface FinancialState {
	age: string;
	// CASH on hand. Rides the engine's cashAssets: counts toward the freedom
	// number, never compounds.
	savings: string;
	// The generic invested pile — money invested but not broken into holdings.
	// Compounds at the global return rate. Edited on the Investments tab.
	investedSavings: string;
	// Liquid cash the user has ALREADY earmarked as runway/emergency money.
	// Distinct from savings (total cash). Drives Money Flow's "current runway".
	// "" / "0" reads as none saved yet.
	currentRunwaySaved: string;
	incomeEntries: IncomeEntry[];
	expenseEntries: ExpenseEntry[];
	// Where surplus goes. Descriptive only; never reduces surplus.
	surplusAllocations: SurplusAllocationEntry[];
	holdings: StockHolding[];
	// Non-growing net worth assets (home, vehicle, other).
	lifestyleAssets: LifestyleAsset[];
	equityGrants: EquityGrant[];
	assumptions: Assumptions;
	// Persisted schema version. Bumped when the shape changes so migration can
	// branch deterministically instead of inferring from field presence.
	_schemaVersion: number;
	_lastUpdated: number;
	_syncStatus: 'idle' | 'pending' | 'synced' | 'error';
}

// The single cash_savings allocation backing Money Flow's "Cash runway" card.
// Stable id so upserts never duplicate it.
export const CASH_RUNWAY_ALLOCATION_ID = 'cash-runway';
